import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCookieValue } from './utils';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return dateString;
  }
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'resolved':
      return '#4CAF50';
    case 'acknowledged':
      return '#FF9800';
    case 'triggered':
      return '#F44336';
    default:
      return '#757575';
  }
};

const getUrgencyColor = (urgency) => {
  switch (urgency?.toLowerCase()) {
    case 'high':
      return '#F44336';
    case 'medium':
      return '#FF9800';
    case 'low':
      return '#4CAF50';
    default:
      return '#757575';
  }
};

export const PagerAlertHistory = () => {
  const navigate = useNavigate();
  const [pagerData, setPagerData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [limit, setLimit] = useState(100);

  const fetchData = async () => {
    const accessToken = getCookieValue('accessToken');
    if (!accessToken) {
      navigate('/login');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    try {
      const params = { limit };
      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/get-pager-alert-history`,
        {
          headers: { Authorization: accessToken },
          params: params
        }
      );

      if (response?.data?.status) {
        setPagerData(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        setErrorMessage(response?.data?.statusMsg || 'Unable to fetch PagerDuty alert history');
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        navigate('/login');
        return;
      }
      setErrorMessage('Failed to fetch PagerDuty alert history');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter, limit]);

  return (
    <div className="pager-alert-container">
      <div className="pager-alert-header">
        <h2>PagerDuty Alert History</h2>
        <div className="pager-alert-controls">
          <label style={{ marginRight: '12px', fontSize: '12px' }}>
            Status:
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ marginLeft: '6px', padding: '4px 6px', fontSize: '12px' }}
            >
              <option value="">All</option>
              <option value="triggered">Triggered</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="resolved">Resolved</option>
            </select>
          </label>
          <label style={{ marginRight: '12px', fontSize: '12px' }}>
            Limit:
            <input
              type="number"
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value) || 100)}
              min="10"
              max="1000"
              style={{ marginLeft: '6px', padding: '4px 6px', fontSize: '12px', width: '80px' }}
            />
          </label>
          <button onClick={fetchData} disabled={isLoading} style={{ padding: '4px 8px', fontSize: '12px' }}>
            Refresh
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="pager-alert-loading">Loading PagerDuty alert history...</div>
      )}
      {errorMessage && (
        <div className="pager-alert-error">{errorMessage}</div>
      )}

      <div className="pager-alert-table-wrapper">
        <table className="pager-alert-table">
          <thead>
            <tr>
              <th>Incident #</th>
              <th>Title</th>
              <th>Service</th>
              <th>Status</th>
              <th>Urgency</th>
              <th>Priority</th>
              <th>Created At</th>
              <th>Acknowledged At</th>
              <th>Resolved At</th>
              <th>Assigned To</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pagerData.length === 0 && !isLoading && (
              <tr>
                <td colSpan="11" style={{ textAlign: 'center', padding: '20px' }}>
                  No PagerDuty incidents found
                </td>
              </tr>
            )}
            {pagerData.map((incident, index) => (
              <tr key={incident.incident_id || index}>
                <td>{incident.incident_number || 'N/A'}</td>
                <td style={{ maxWidth: '300px', wordWrap: 'break-word' }}>
                  {incident.title || 'N/A'}
                </td>
                <td>{incident.service_name || 'N/A'}</td>
                <td>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: '3px',
                      backgroundColor: getStatusColor(incident.status),
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: '500'
                    }}
                  >
                    {incident.status || 'N/A'}
                  </span>
                </td>
                <td>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: '3px',
                      backgroundColor: getUrgencyColor(incident.urgency),
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: '500'
                    }}
                  >
                    {incident.urgency || 'N/A'}
                  </span>
                </td>
                <td>
                  {incident.priority && typeof incident.priority === 'object' && incident.priority.name
                    ? incident.priority.name
                    : incident.priority || 'N/A'}
                </td>
                <td>{formatDate(incident.created_at)}</td>
                <td>{formatDate(incident.acknowledged_at)}</td>
                <td>{formatDate(incident.resolved_at)}</td>
                <td>
                  {Array.isArray(incident.assigned_to) && incident.assigned_to.length > 0
                    ? incident.assigned_to.join(', ')
                    : incident.assigned_to && typeof incident.assigned_to === 'object' && incident.assigned_to.summary
                    ? incident.assigned_to.summary
                    : incident.assigned_to || 'N/A'}
                </td>
                <td>
                  {incident.html_url && (
                    <a
                      href={incident.html_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#2196F3', textDecoration: 'none', fontSize: '12px' }}
                    >
                      View
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
